import { groupMediaByMonth } from "@couples/shared";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Image as ImageIcon } from "lucide-react";
import { useMemo, useState } from "react";

import { fetchTimelinePage, type TimelineCursor } from "../lib/media/timeline";
import { useSignedUrls } from "../lib/media/useSignedUrls";
import { queryKeys } from "../lib/queryKeys";
import { useLoadMoreSentinel } from "../lib/useLoadMoreSentinel";
import MediaTile, { VideoBadge } from "./MediaTile";
import OverlayShell from "./OverlayShell";
import QueryState from "./QueryState";

interface Props {
  /** 既に結び付けてあるメディアID。開いた時点で選択済みにする */
  initialSelected: string[];
  onDone: (mediaIds: string[]) => void;
  onClose: () => void;
}

/**
 * アルバムにある写真・動画を選ぶ面。交換日記から結び付けるのに使う。
 *
 * 選択は常時オン(選ぶために開く面なので、選択モードの切り替えを持たない)。
 * 一覧はタイムラインと同じキー・同じ問い合わせに乗せているため、
 * アルバムを見た後に開いても読み直しが起きない。
 */
export default function MediaLinkPicker({ initialSelected, onDone, onClose }: Props) {
  // 決めるまでは呼び出し側の状態を触らない。閉じれば選び直しは無かったことになる
  const [selected, setSelected] = useState<string[]>(initialSelected);

  const timeline = useInfiniteQuery({
    queryKey: queryKeys.timeline({ kind: "all" }, null, null, null),
    queryFn: ({ pageParam }) => fetchTimelinePage(pageParam),
    initialPageParam: null as TimelineCursor | null,
    getNextPageParam: (page) => page.next,
  });

  const items = useMemo(
    () => timeline.data?.pages.flatMap((page) => page.items) ?? [],
    [timeline.data],
  );
  const groups = useMemo(
    // グルーピングは takenAt しか見ないため、列名だけ合わせて渡す(→ routes/Timeline.tsx)
    () => groupMediaByMonth(items.map((item) => ({ ...item, takenAt: item.taken_at }))),
    [items],
  );

  const thumbKeys = useMemo(() => items.map((item) => item.thumbnail_key), [items]);
  const { urls: thumbUrls, refresh: refreshThumbs } = useSignedUrls(thumbKeys);

  const sentinel = useLoadMoreSentinel(timeline);

  function toggle(mediaId: string) {
    setSelected((current) =>
      current.includes(mediaId)
        ? current.filter((id) => id !== mediaId)
        : // 並びは選んだ順。日記のカードにもこの順で出す
          [...current, mediaId],
    );
  }

  return (
    <OverlayShell title="アルバムから選ぶ" icon={ImageIcon} onClose={onClose} width="grid" elevated>
      {/* 下の決定バーが被さるぶんの逃げ幅(PC はバーがカード内に収まる) */}
      <div className="flex w-full flex-col gap-3 p-4 pb-24 xl:pb-4">
        <QueryState
          isPending={timeline.isPending}
          isError={timeline.isError}
          errorMessage="アルバムの取得に失敗しました。"
          isEmpty={timeline.isSuccess && items.length === 0}
          emptyMessage="アルバムに写真・動画がありません。"
          emptyClassName="p-10 text-center text-sm"
        />

        {groups.map((group) => (
          <section key={group.month} className="flex flex-col gap-2">
            <header className="flex items-center gap-2">
              <span className="text-sm font-medium">{group.label}</span>
              <span className="h-px flex-1 bg-[var(--color-line)]" />
            </header>
            <div className="grid grid-cols-3 gap-1.5 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-[repeat(auto-fill,minmax(180px,1fr))]">
              {group.items.map((item) => (
                <MediaTile
                  key={item.id}
                  thumbUrl={thumbUrls[item.thumbnail_key]}
                  onThumbError={refreshThumbs}
                  showCheck
                  isSelected={selected.includes(item.id)}
                  onClick={() => {
                    toggle(item.id);
                  }}
                >
                  {item.type === "video" && <VideoBadge durationSeconds={item.duration_seconds} />}
                </MediaTile>
              ))}
            </div>
          </section>
        ))}

        <div ref={sentinel} className="h-8" aria-hidden />
        {timeline.isFetchingNextPage && (
          <p className="text-center text-xs text-[var(--color-sub)]">読み込み中...</p>
        )}
      </div>

      {/* 全画面のスマホ・タブレットは画面下部に留める(fixed)。モーダルの PC はカードの中に収める */}
      <div className="fixed inset-x-0 bottom-0 z-10 flex items-center justify-center gap-3 border-t border-[var(--color-line)] bg-white p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] xl:sticky xl:mt-auto">
        <span className="text-xs text-[var(--color-sub)] tabular-nums">{selected.length}件</span>
        <button
          type="button"
          onClick={() => {
            onDone(selected);
          }}
          className="rounded-lg bg-[var(--color-accent)] px-4 py-2 text-sm font-medium text-white transition hover:opacity-85"
        >
          決定
        </button>
      </div>
    </OverlayShell>
  );
}
